import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import { queryClient } from '@/lib/queryClient';
import { isUnauthorizedError } from '@/lib/authUtils';
import { trpc, trpcClient } from '@/lib/trpc';
import { Play, Activity, AlertCircle, RefreshCw } from 'lucide-react-native';
import { colors, typography } from '@/lib/theme';

interface ScoreUpdaterStatusResponse {
  provider?: string;
  isRunning: boolean;
  isUpdating: boolean;
  interval: number | null;
  activeGameCount?: number;
  apiNfl?: {
    requestsRemaining: number | null;
    lastSuccessAt: string | null;
    lastErrorKind: string | null;
  };
}

export default function ScoreUpdaterStatus() {
  const { toast } = useToast();
  const contentStyle = { gap: 12 };
  const statStyle = { flex: 1 };
  const buttonGroupStyle = { paddingTop: 8 };

  const { data: status, isLoading, error } = useQuery(
    trpc.admin.scoreUpdaterStatus.queryOptions(undefined, {
      refetchInterval: 30000,
    }),
  );

  const triggerUpdateMutation = useMutation({
    mutationFn: () => trpcClient.admin.triggerScoreUpdater.mutate(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.admin.scoreUpdaterStatus.queryFilter());
      toast.show({
        title: "Score Update Triggered",
        description: "Manual score update has been triggered successfully.",
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to trigger score update. Please try again.",
        variant: "destructive",
      });
    },
  });

  const formatInterval = (intervalMs: number | null): string => {
    if (!intervalMs) return "N/A";
    const minutes = Math.floor(intervalMs / 60000);
    const seconds = Math.floor((intervalMs % 60000) / 1000);
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const getStatusBadge = (status: ScoreUpdaterStatusResponse | undefined) => {
    if (!status) return null;

    if (status.isUpdating) {
      return (
        <Badge variant="default">
          <View style={styles.badgeRow}>
            <RefreshCw size={12} color={colors.text.primary} style={styles.badgeIcon} />
            <Text style={styles.badgeText}>Updating</Text>
          </View>
        </Badge>
      );
    }

    if (status.isRunning) {
      return (
        <Badge variant="secondary">
          <View style={styles.badgeRow}>
            <Activity size={12} color={colors.success[700]} style={styles.badgeIcon} />
            <Text style={[styles.badgeText, styles.successText]}>Running</Text>
          </View>
        </Badge>
      );
    }

    return (
      <Badge variant="outline">
        <View style={styles.badgeRow}>
          <AlertCircle size={12} color={colors.text.secondary} style={styles.badgeIcon} />
          <Text style={styles.mutedBadgeText}>Idle</Text>
        </View>
      </Badge>
    );
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <View style={styles.titleRow}>
              <Activity size={20} color={colors.text.primary} style={styles.titleIcon} />
              <Text style={styles.titleText}>
                Automatic Score Updates
              </Text>
            </View>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Text style={styles.errorText}>Failed to load score updater status</Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <View style={styles.headerRow}>
            <View style={styles.titleRow}>
              <Activity size={20} color={colors.text.primary} style={styles.titleIcon} />
              <Text style={styles.titleText}>
                Automatic Score Updates
              </Text>
            </View>
            {getStatusBadge(status)}
          </View>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <View style={styles.skeletonWrap}>
            <View style={styles.skeletonLong} />
            <View style={styles.skeletonShort} />
          </View>
        ) : (
          <View style={contentStyle}>
            <Text style={styles.bodyText}>
              Scores are automatically updated from API-NFL during active game windows and
              reconciled through the provider&apos;s live schedule.
            </Text>

            {status && (
              <View style={styles.statsRow}>
                <View style={statStyle}>
                  <Text style={styles.labelText}>Status:</Text>
                  <Text style={styles.valueText}>
                    {status.isUpdating ? "Updating..." : status.isRunning ? "Active" : "Idle"}
                  </Text>
                </View>
                <View style={statStyle}>
                  <Text style={styles.labelText}>Update Interval:</Text>
                  <Text style={styles.valueText}>{formatInterval(status.interval)}</Text>
                </View>
              </View>
            )}

            {status && (
              <Text style={styles.bodyText}>
                Provider: {status.provider ?? "API-NFL"} · Active games: {status.activeGameCount ?? 0} ·
                Requests remaining: {status.apiNfl?.requestsRemaining ?? "unknown"}
              </Text>
            )}

            <View style={buttonGroupStyle}>
              <Button
                variant="outline"
                onPress={() => triggerUpdateMutation.mutate()}
                disabled={triggerUpdateMutation.isPending || status?.isUpdating}
                style={styles.buttonRow}
              >
                <Play size={16} color={colors.text.primary} style={styles.titleIcon} />
                <Text style={styles.buttonText}>
                  {triggerUpdateMutation.isPending ? "Triggering..." : "Trigger Manual Update"}
                </Text>
              </Button>
            </View>
          </View>
        )}
      </CardContent>
    </Card>
  );
}

const styles = StyleSheet.create(() => ({
  badgeIcon: {
    marginRight: 4,
  },
  badgeRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  badgeText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  bodyText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    lineHeight: 20,
  },
  buttonRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  buttonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  errorText: {
    color: colors.error[600],
    fontFamily: typography.fontFamily.bodyMedium,
  },
  headerRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: 8,
  },
  labelText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontWeight: '700',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  mutedBadgeText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  skeletonLong: {
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 1,
    height: 16,
    marginBottom: 8,
    width: '75%',
  },
  skeletonShort: {
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 1,
    height: 16,
    width: '50%',
  },
  skeletonWrap: {
    opacity: 0.6,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 16,
  },
  successText: {
    color: colors.success[700],
  },
  titleIcon: {
    marginRight: 8,
  },
  titleRow: {
    alignItems: 'center',
    flexBasis: '65%',
    flexDirection: 'row',
    flexShrink: 1,
    minWidth: 220,
  },
  titleText: {
    color: colors.text.primary,
    flexShrink: 1,
    fontFamily: typography.fontFamily.display,
    fontSize: 18,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  valueText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
  },
}));
